/*
 * File: Typography.styles.js
 * Project: lets-talk-web
 *
 * Created by Brendan Michaelsen on January 27, 2022 at 10:52 AM
 * Copyright © 2022 Let's Talk. All rights reserved.
 *
 * Last Modified: April 29, 2024 at 10:06 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import styled, { css } from 'styled-components';

// Constants
import { mediumBreakpoint, largeBreakpoint } from '../../styles/constants';


/**
 * Constants
 */

export const WEIGHTS = {
	extralight: 200,
	light: 300,
	regular: 400,
	medium: 500,
	semibold: 600,
	bold: 700,
	extrabold: 800,
	black: 900
};

export const SIZES = {
	h1: {
		small: '1.7rem', // 48px
		medium: '2.5rem', // 48px
		large: '3.0rem', // 48px
	},
	h1_1: {
		small: '1.7rem', // 38px
		medium: '2.0rem', // 38px
		large: '2.5rem', // 38px
	},
	h1_2: {
		small: '2.0rem', // 38px
		medium: '2.8rem', // 38px
		large: '3.5rem', // 38px
	},
	h2: {
		small: '1.625rem', // 34px
		medium: '1.875rem', // 34px
		large: '2.125rem', // 34px
	},
	h3: {
		small: '1.45rem', // 28px
		medium: '1.6rem', // 28px
		large: '1.75rem', // 28px
	},
	h4: {
		small: '1.175rem', // 22px
		medium: '1.275rem', // 22px
		large: '1.375rem', // 22px
	},
	h5: {
		small: '1rem', // 16px
		medium: '1rem', // 16px
		large: '1rem', // 16px
	},
	h6: {
		small: '0.75rem', // 12px
		medium: '0.75rem', // 12px
		large: '0.75rem', // 12px
	},
	p_1: {
		small: '1rem', // 16px
		medium: '1rem', // 16px
		large: '1rem', // 16px
	},
	p_2: {
		small: '0.875rem', // 12px
		medium: '0.875rem', // 12px
		large: '0.875rem', // 12px
	},
	p_3: {
		small: '0.6875rem', // 11px
		medium: '0.6875rem', // 11px
		large: '0.6875rem', // 11px
	},
	span_1: {
		small: '1rem', // 16px
		medium: '1rem', // 16px
		large: '1rem', // 16px
	},
	span_2: {
		small: '0.75rem', // 12px
		medium: '0.75rem', // 12px
		large: '0.75rem', // 12px
	},
	span_3: {
		small: '0.6875rem', // 11px
		medium: '0.6875rem', // 11px
		large: '0.6875rem', // 11px
	},
	'span_button-medium': {
		small: '0.875rem', // 14px
		medium: '0.875rem', // 14px
		large: '0.875rem', // 14px
	},
	'span_button-small': {
		small: '0.75rem', // 12px
		medium: '0.75rem', // 12px
		large: '0.75rem', // 12px
	},
	label: {
		small: '0.75rem', // 12px
		medium: '0.75rem', // 12px
		large: '0.75rem', // 12px
	},
};

export const LINE_HEIGHTS = {
	h1: 'normal',
	h2: 'normal',
	h3: '160%',
	h4: '140%',
	h5: '140%',
	h6: '160%',
	p_1: '160%',
	p_2: '160%',
	p_3: '160%',
	span_1: 'normal',
	span_2: 'normal',
	span_3: 'normal',
	'span_button-medium': 'normal',
	'span_button-small': 'normal',
	label: 'normal',
};


/**
 * Styles
 */

export const Typography = styled.span`
 	font-weight: ${({ weight }) => WEIGHTS[weight]};
 	line-height: ${({ as, variation }) => (as === 'span' || as === 'p' || as === 'h1' ? LINE_HEIGHTS[`${as}${variation ? `_${variation}` : ''}`] : LINE_HEIGHTS[as])};
 	margin-bottom: ${({ as }) => (as === 'label' ? '0.375em' : '0')};
 	text-align: ${({ center }) => (center ? 'center' : 'inherit')};
	font-size: ${({ as, variation }) => (as === 'span' || as === 'p' || as === 'h1' ? SIZES[`${as}${variation ? `_${variation}` : ''}`].small : SIZES[as].small)};
	font-style: ${({ fontStyle }) => fontStyle};
	text-decoration: ${({ decoration }) => decoration};

	@media only screen and (min-width: ${mediumBreakpoint}em) {
		font-size: ${({ as, variation }) => (as === 'span' || as === 'p' || as === 'h1' ? SIZES[`${as}${variation ? `_${variation}` : ''}`].medium : SIZES[as].medium)};
	}
	@media only screen and (min-width: ${largeBreakpoint}em) {
		font-size: ${({ as, variation }) => (as === 'span' || as === 'p' || as === 'h1' ? SIZES[`${as}${variation ? `_${variation}` : ''}`].large : SIZES[as].large)};
	}

 	${({ truncate }) => truncate && css`
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
	`}
`;
